'use client';

import { Country, CountryLabel, setCountryCookie, setCurrencyCookie, useRegionCurrency, getLanguageCookie, setLanguageCookie, CLASSNAME } from '@skin-seoul/service';
import { CountryCurrency } from '@skin-seoul/service';
import { Button, createListCollection, Dialog, VStack, Select, useSelectContext, Flex } from '@skin-seoul/design-system';
import { useRef, useState } from 'react';
import { LucideIcon } from '../LucideIcon';
import { getObjectValues } from '@skin-seoul/utils';
import TranslateLanguageSelect from '../TranslateLanguageSelect/TranslateLanguageSelect';
type CountryChangeModalProps = {
  initialCountry: Country;
  disableReload?: boolean;
  isOpen: boolean;
  onClose: () => void;
};
const SelectValue = () => {
  const select = useSelectContext();
  const items = select.selectedItems as Array<{
    label: string;
    value: string;
  }>;
  const {
    label,
    value
  } = items[0];
  return <Select.ValueText placeholder="Select member" textStyle="paragraph.small" color="neutral.900" overflowY="visible" data-sentry-element="Select.ValueText" data-sentry-component="SelectValue" data-sentry-source-file="CountryChangeModal.tsx">
      <Flex w="fit-content" gap={1} data-sentry-element="Flex" data-sentry-source-file="CountryChangeModal.tsx">
        {label} <Flex w="fit-content" data-sentry-element="Flex" data-sentry-source-file="CountryChangeModal.tsx">({CountryCurrency[value as Country]})</Flex>
      </Flex>
    </Select.ValueText>;
};
export const CountryChangeModal = ({
  initialCountry,
  isOpen,
  onClose,
  disableReload = false
}: CountryChangeModalProps) => {
  const [selectedCountry, setSelectedCountry] = useState<Country[]>([initialCountry]);
  const [selectedLanguage, setSelectedLanguage] = useState<string | undefined>(getLanguageCookie());
  const shippingCountry = getObjectValues(Country);
  const {
    setCountry
  } = useRegionCurrency();
  const countryInputRef = useRef<HTMLButtonElement>(null);
  const countryItems = shippingCountry.map(country => ({
    label: CountryLabel[country],
    value: country
  }));
  const sortedCountryItems = countryItems.sort((a, b) => a.label.localeCompare(b.label));
  const isButtonActive = selectedCountry[0] !== initialCountry || selectedLanguage !== getLanguageCookie();
  const countryCollection = createListCollection({
    items: sortedCountryItems
  });
  const handleOnSave = (value: Country[]) => {
    setCountryCookie(value[0]);
    setCurrencyCookie(CountryCurrency[value[0]]);
    setCountry(value[0]);
    if (selectedLanguage) {
      setLanguageCookie(selectedLanguage);
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('ss:user-language-change', {
          detail: selectedLanguage
        }));
      }
    }
    onClose();
    if (!disableReload) {
      window.location.reload();
    }
  };
  return <Dialog.Root open={isOpen} motionPreset="scale" initialFocusEl={() => countryInputRef.current} data-sentry-element="Dialog.Root" data-sentry-component="CountryChangeModal" data-sentry-source-file="CountryChangeModal.tsx">
      <Dialog.Backdrop backgroundColor="generic.100/20" backdropFilter="blur(2px)" data-sentry-element="Dialog.Backdrop" data-sentry-source-file="CountryChangeModal.tsx" />
      <Dialog.Positioner data-sentry-element="Dialog.Positioner" data-sentry-source-file="CountryChangeModal.tsx">
        <Dialog.Content borderRadius="4px" marginX={5} maxWidth={{
        base: '90%',
        mobile: '600px'
      }} data-sentry-element="Dialog.Content" data-sentry-source-file="CountryChangeModal.tsx">
          <Dialog.Header backgroundColor="primary.600" display="flex" flexDirection="row" paddingLeft={4} paddingRight={2} paddingY={2} alignItems="center" borderTopRadius="4px" justifyContent="space-between" data-sentry-element="Dialog.Header" data-sentry-source-file="CountryChangeModal.tsx">
            <Dialog.Title textStyle="paragraph.medium" fontWeight="medium" color="generic.0" data-sentry-element="Dialog.Title" data-sentry-source-file="CountryChangeModal.tsx">
              Choose Your Location
            </Dialog.Title>
            <Button variant="ghost" onClick={onClose} _hover={{
            backgroundColor: 'transparent'
          }} maxWidth="40px" _icon={{
            color: 'white'
          }} size="md" data-sentry-element="Button" data-sentry-source-file="CountryChangeModal.tsx">
              <LucideIcon name="X" data-sentry-element="LucideIcon" data-sentry-source-file="CountryChangeModal.tsx" />
            </Button>
          </Dialog.Header>
          <Dialog.Body paddingX="16px" paddingY="20px" data-sentry-element="Dialog.Body" data-sentry-source-file="CountryChangeModal.tsx">
            <VStack gap={3} w="100%" h="100%" data-sentry-element="VStack" data-sentry-source-file="CountryChangeModal.tsx">
              <Select.Root multiple={false} collection={countryCollection} deselectable={false} value={selectedCountry} onValueChange={value => {
              setSelectedCountry(value.value as Country[]);
            }} positioning={{
              sameWidth: true,
              offset: {
                mainAxis: 0
              }
            }} data-sentry-element="Select.Root" data-sentry-source-file="CountryChangeModal.tsx">
                <Select.HiddenSelect data-sentry-element="Select.HiddenSelect" data-sentry-source-file="CountryChangeModal.tsx" />
                <Select.Label textStyle="paragraph.small" color="neutral.900" data-sentry-element="Select.Label" data-sentry-source-file="CountryChangeModal.tsx">
                  Shipping Country and Currency
                </Select.Label>
                <Select.Control data-sentry-element="Select.Control" data-sentry-source-file="CountryChangeModal.tsx">
                  <Select.Trigger ref={countryInputRef} border="1px solid" borderColor="neutral.900" _hover={{
                  borderColor: 'neutral.300'
                }} className={CLASSNAME.NOT_TRANSLATE} data-sentry-element="Select.Trigger" data-sentry-source-file="CountryChangeModal.tsx">
                    <SelectValue data-sentry-element="SelectValue" data-sentry-source-file="CountryChangeModal.tsx" />
                  </Select.Trigger>
                  <Select.IndicatorGroup data-sentry-element="Select.IndicatorGroup" data-sentry-source-file="CountryChangeModal.tsx">
                    <Select.Indicator color="neutral.900" data-sentry-element="Select.Indicator" data-sentry-source-file="CountryChangeModal.tsx" />
                  </Select.IndicatorGroup>
                </Select.Control>

                <Select.Positioner data-sentry-element="Select.Positioner" data-sentry-source-file="CountryChangeModal.tsx">
                  <Select.Content maxHeight="30vh" boxShadow="0px 0px 10px 0px rgba(0, 0, 0, 0.1)" className={CLASSNAME.NOT_TRANSLATE} data-sentry-element="Select.Content" data-sentry-source-file="CountryChangeModal.tsx">
                    {countryItems.map(country => <Select.Item key={country.value} item={country} _selected={{
                    backgroundColor: 'neutral.100/60'
                  }} w="100%">
                        <Flex w="fit-content" gap={1}>
                          {country.label} <Flex w="fit-content">({CountryCurrency[country.value]})</Flex>
                        </Flex>
                        <Select.ItemIndicator />
                      </Select.Item>)}
                  </Select.Content>
                </Select.Positioner>
              </Select.Root>
              <TranslateLanguageSelect value={selectedLanguage} onChange={setSelectedLanguage} applyImmediately={false} data-sentry-element="TranslateLanguageSelect" data-sentry-source-file="CountryChangeModal.tsx" />
            </VStack>
          </Dialog.Body>
          <Dialog.Footer data-sentry-element="Dialog.Footer" data-sentry-source-file="CountryChangeModal.tsx">
            <Button variant="outline" size="sm" color="neutral.900" border="1px solid" borderColor="neutral.200" _hover={{
            backgroundColor: 'neutral.100/60'
          }} textStyle="paragraph.small" onClick={onClose} data-sentry-element="Button" data-sentry-source-file="CountryChangeModal.tsx">
              Cancel
            </Button>

            <Button variant="primary" size="sm" textStyle="paragraph.small" backgroundColor="neutral.900" _hover={{
            backgroundColor: 'neutral.800'
          }} disabled={!isButtonActive} onClick={() => handleOnSave(selectedCountry)} data-sentry-element="Button" data-sentry-source-file="CountryChangeModal.tsx">
              Save
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>;
};